import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {TimeProvider} from '../context/time-context';
import {useGetSchedulableWindowsQuery} from '../features/api/scheduleWindowsApiSlice';
import {
  monthDecremented,
  monthIncremented,
  weekDecremented,
  weekIncremented,
} from '../features/scheduling/schedulerSlice';
import AppointmentPicker from './appointment-picker';
import RouteContainer from './route-container';
import StoreIcon from './store-icon';

function Time(props) {
  const dispatch = useDispatch();
  const {repair, group} = useRepair();
  const scheduler = useSelector((state) => state.scheduler);
  const {refetchSignal} = props;

  const {
    data,
    isFetching: fetchingWindows,
    refetch: refetchWindows,
  } = useGetSchedulableWindowsQuery({
    neededDays: scheduler.neededDays,
    repairData: {repair, group},
    targetDay: scheduler.targetDay,
  });

  // Refetch time windows when refetchSignal changes
  useEffect(() => {
    if (refetchSignal !== null) {
      refetchWindows();
    }
  }, [refetchSignal, refetchWindows]);

  const {timeZone} = repair;
  const {selectedDay} = scheduler;
  const pageText = usePageText();

  const {
    time: {onSiteTitle, onSiteSubtitle, inStoreTitle, inStoreSubtitle, changeStore, scheduleHeader},
  } = pageText;

  const changeSelectedTime = (action, value) => {
    if (value === 'month') {
      if (action === 'increment') {
        dispatch(monthIncremented({windowsData: {...data}, timeZone}));
      } else if (action === 'decrement') {
        dispatch(monthDecremented());
      }
    }
    if (value === 'week') {
      if (action === 'increment') {
        dispatch(weekIncremented({windowsData: {...data}, timeZone}));
      } else if (action === 'decrement') {
        dispatch(weekDecremented());
      }
    }
  };

  function renderAppointmentPicker() {
    const {validatingWindow} = props;

    return (
      <TimeProvider value={{...data, fetchingWindows}}>
        <AppointmentPicker
          decrementMonth={() => changeSelectedTime('decrement', 'month')}
          decrementWeek={() => changeSelectedTime('decrement', 'week')}
          incrementMonth={() => changeSelectedTime('increment', 'month')}
          incrementWeek={() => changeSelectedTime('increment', 'week')}
          selectedDay={selectedDay}
          timeZone={timeZone}
          validatingWindow={validatingWindow}
        />
      </TimeProvider>
    );
  }

  const {appointmentPickerOnly = false} = props;

  if (appointmentPickerOnly) {
    return renderAppointmentPicker();
  }

  let title = '';
  switch (props.serviceOption) {
    case 'ON_SITE':
      title = onSiteTitle;
      break;
    case 'IN_STORE':
    default:
      break;
  }

  if (props.serviceOption === 'ON_SITE') {
    return (
      <div className={styles.fullWidthContainer}>
        <RouteContainer title={title}>
          {!props.disableAltTimes && <h2 className={styles.blueHeader}>{onSiteSubtitle}</h2>}
          {renderAppointmentPicker()}
        </RouteContainer>
      </div>
    );
  } else if (props.serviceOption === 'IN_STORE') {
    const storeName = props.selectedStore.name;
    const storeAddress = props.selectedStore.displayAddress;

    return (
      <div className={styles.fullWidthContainer}>
        <div className={styles.mainContainer}>
          <h1 className={styles.title}>{inStoreTitle}</h1>
          {renderAppointmentPicker()}
        </div>
        <div className={styles.sideContainer}>
          <div className={styles.location}>
            IN
            <h3 className={styles.subtitle}>{inStoreSubtitle}</h3>
            <div className={styles.locationRow}>
              <StoreIcon />
              <div className={styles.address}>
                <h4 className={styles.storeName}>{storeName}</h4>
                <p className={styles.storeAddress}>{storeAddress}</p>
                <button onClick={props.handleChangeStore} className={styles.changeStoreButton}>
                  {changeStore}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.schedule}>
            <h3 className={styles.subtitle}>{scheduleHeader}</h3>
            <div className={styles.appointment}>
              {props.start ? (
                <span>
                  <p>{props.start.format('LL, LT z')}</p>
                </span>
              ) : (
                <p className={styles.dash}>-</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

Time.propTypes = {
  validatingWindow: PropTypes.bool,
  appointmentPickerOnly: PropTypes.bool,
  serviceOption: PropTypes.string,
  selectedStore: PropTypes.object,
  disableAltTimes: PropTypes.bool,
  handleChangeStore: PropTypes.func,
  start: PropTypes.object,
};

export default Time;

const styles = {
  fullWidthContainer: 'w-full flex flex-col items-center',
  blueHeader: 'text-blue-600 font-semibold text-lg mb-2',
  mainContainer: 'w-full md:w-2/3 p-4',
  sideContainer: 'w-full md:w-1/3 p-4 mt-6 md:mt-0',
  location: 'bg-gray-100 p-4 rounded-md shadow-sm',
  subtitle: 'text-sm font-medium text-gray-700 mt-2',
  locationRow: 'flex items-start space-x-2 mt-2',
  address: 'ml-2',
  storeName: 'font-semibold text-md',
  storeAddress: 'text-sm text-gray-600',
  changeStoreButton: 'mt-2 text-blue-500 underline text-sm hover:text-blue-700',
  schedule: 'mt-6',
  appointment: 'mt-2 text-gray-800',
  dash: 'text-gray-500 italic',
  title: 'text-xl font-bold mb-4',
};
